/*
 * nc-cart-drawer.js — controller for the first-party cart drawer.
 *
 * Replaces Rebuy's Smart Cart runtime. There is no client-side templating: every
 * change re-renders sections/nc-cart-drawer.liquid through Shopify's Section
 * Rendering API, so what the shopper sees always comes from the real cart.
 *
 * All handlers are delegated from `document`, so a re-render never needs
 * re-binding and can never leave a dead button behind.
 *
 * Exposed as window.NcCartDrawer:
 *   .show() / .hide() / .toggle()
 *   .refresh()                                  re-render from the current cart
 *   .addToCart(variantId, { sellingPlanId, quantity, properties })
 *   .isEnabled()
 *
 * Events dispatched on `document` (the theme's existing convention):
 *   cart:updated     { cart }         after any successful mutation
 *   cart:item-added  { item, cart }   after an add (drives the Klaviyo hook)
 */
(function () {
  'use strict';

  var SECTION_ID = 'nc-cart-drawer';
  var WRAPPER_ID = 'shopify-section-' + SECTION_ID;
  var VISIBLE_CLASS = 'is-visible';
  var BODY_OPEN_CLASS = 'nc-cart-drawer-open';

  // Rebuy's runtime put this on <body> and the vendored baseline
  // (nc-cart-drawer-base.css) hangs the whole open state off it: the flyout
  // slides in from right:-100% only under `.rebuy-cart-visible .rebuy-cart__flyout`
  // (top:100% -> 0 below 480px), and the backdrop only then gets its opacity and
  // pointer-events back. Without it the drawer "opens" off-screen behind an
  // inert backdrop, so this class is load-bearing, not cosmetic.
  var BODY_VISIBLE_CLASS = 'rebuy-cart-visible';

  var routes = window.routes || {};
  var ADD_URL = routes.cart_add_url || '/cart/add.js';
  var CHANGE_URL = routes.cart_change_url || '/cart/change.js';
  var UPDATE_URL = routes.cart_update_url || '/cart/update.js';

  // Was Rebuy's configured copy ('{{code}} nie jest prawidłowy dla tego
  // koszyka.'); replaced on the owner's instruction 2026-08-28. Now shown only
  // for codes Shopify does not know — see discountRejectionMessage().
  var DISCOUNT_INVALID_MESSAGE = 'Taki kupon rabatowy nie istnieje';

  // /cart.js only says `applicable: false`; the Storefront API's cart warnings
  // (2025-07+) say why. Tokenless access covers cart mutations, and the Ajax
  // cart token (with its ?key=) is a valid Storefront cart ID — same cart.
  var STOREFRONT_API_URL = '/api/2026-07/graphql.json';
  var DISCOUNT_REASON_TIMEOUT_MS = 3000;
  var DISCOUNT_REASON_QUERY =
    'mutation ($cartId: ID!, $codes: [String!]!) {' +
    ' cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $codes) {' +
    ' cart { discountCodes { code applicable } } warnings { code message } } }';

  // Marketing's request 2026-10-23: a code refused because the cart holds
  // subscriptions or multipacks must not read as "this code does not exist".
  // {code} is the shopper's code, {other} the code already applied.
  var DISCOUNT_REASON_MESSAGES = {
    DISCOUNT_NOT_FOUND: DISCOUNT_INVALID_MESSAGE,
    DISCOUNT_NO_ENTITLED_LINE_ITEMS:
      'Kod {code} nie obejmuje produktów w Twoim koszyku. Kody rabatowe nie łączą się z subskrypcją ani z zestawami i kuracjami, które mają już rabat w cenie.',
    SUBSCRIPTIONS_ONLY: 'Kod {code} nie łączy się z subskrypcją — subskrypcja ma już rabat w cenie.',
    DISCOUNT_CODE_NOT_HONOURED: 'Kod {code} nie łączy się z rabatem, który już działa w Twoim koszyku.',
    WITH_OTHER_CODE: 'Kody rabatowe nie łączą się ze sobą — w Twoim koszyku działa już kod {other}.',
    DISCOUNT_CURRENTLY_INACTIVE: 'Kod {code} nie jest już aktywny.',
    DISCOUNT_USAGE_LIMIT_REACHED: 'Kod {code} został już wykorzystany maksymalną liczbę razy.',
    DISCOUNT_CUSTOMER_USAGE_LIMIT_REACHED: 'Kod {code} został już przez Ciebie wykorzystany.',
    DISCOUNT_CUSTOMER_NOT_ELIGIBLE: 'Kod {code} nie jest dostępny dla Twojego konta.',
    DISCOUNT_ELIGIBLE_CUSTOMER_MISSING: 'Kod {code} jest przypisany do konta klienta — zaloguj się, aby z niego skorzystać.',
    DISCOUNT_PURCHASE_NOT_IN_RANGE: 'Wartość koszyka nie spełnia warunków kodu {code}.',
    DISCOUNT_QUANTITY_NOT_IN_RANGE: 'Liczba produktów w koszyku nie spełnia warunków kodu {code}.',
    DISCOUNT_NO_ENTITLED_SHIPPING_LINES: 'Kod {code} dotyczy dostawy — wpisz go w kasie, po wybraniu sposobu dostawy.',
    UNKNOWN: 'Kodu {code} nie można użyć w tym koszyku.',
  };
  DISCOUNT_REASON_MESSAGES.DISCOUNT_INCOMPATIBLE_PURCHASE_TYPE =
    DISCOUNT_REASON_MESSAGES.DISCOUNT_NO_ENTITLED_LINE_ITEMS;

  var isOpen = false;
  var lastFocused = null;

  // Mutations are serialised rather than dropped: a shopper tapping "+" three
  // times quickly must end up at +3, not at whichever click won the race.
  var queue = Promise.resolve();

  // Quantities the server has not confirmed yet. Without this, two fast clicks
  // would both read the same quantity out of the DOM and the second would
  // overwrite the first instead of building on it.
  var optimisticQuantities = {};

  // Survives the re-render that follows a rejected code, so the shopper keeps
  // both their typed value and the reason it failed.
  var pendingDiscount = null;

  // "Is there a drawer on this page?" — the section is rendered site-wide from
  // layout/theme.liquid, so this is only false if that render failed. Callers
  // (global.js, nc-product-tile.js) use it to decide between the ajax path and
  // a native form submit, so it must answer for the page, not for a setting.
  function enabled() {
    return !!root();
  }

  function wrapper() {
    return document.getElementById(WRAPPER_ID);
  }

  function root() {
    return document.getElementById('rebuy-cart');
  }

  /* ---------------------------------------------------------------- rendering */

  function applyRenderedHTML(html) {
    var host = wrapper();
    if (!host || !html) return;

    // The Section Rendering API returns the section wrapped in its own
    // #shopify-section-* div; take the inner markup so we do not nest wrappers.
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var incoming = parsed.getElementById(WRAPPER_ID) || parsed.body;

    host.innerHTML = incoming.innerHTML;

    // Everything below lives on elements we just replaced.
    optimisticQuantities = {};
    if (isOpen) markOpen();
    restorePendingDiscount();
  }

  function restorePendingDiscount() {
    if (!pendingDiscount) return;

    var input = document.getElementById('rebuy-cart__discount-input');
    var error = document.querySelector('[data-nc-discount-error]');

    if (input) {
      input.value = pendingDiscount.code;
      var wrap = input.closest('.rebuy-input-wrapper--floating-label');
      if (wrap) wrap.classList.add('show-label');
    }
    if (error) {
      error.textContent = pendingDiscount.message;
      error.hidden = false;
    }
  }

  function refresh() {
    return fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        applyRenderedHTML(sections[SECTION_ID]);
      })
      .catch(function (error) {
        console.error('[nc-cart-drawer] refresh failed', error);
      });
  }

  /* ---------------------------------------------------------------- open/close */

  function markOpen() {
    var el = root();
    if (!el) return;
    el.classList.add(VISIBLE_CLASS);
    el.setAttribute('aria-hidden', 'false');
  }

  function show() {
    var el = root();
    // Nothing to open — the caller should let the link fall through to /cart.
    if (!el) return false;

    if (!isOpen) lastFocused = document.activeElement;
    isOpen = true;
    markOpen();
    document.body.classList.add(BODY_OPEN_CLASS, BODY_VISIBLE_CLASS);

    var close = el.querySelector('#rebuy-cart-close');
    if (close) close.focus();

    document.dispatchEvent(new CustomEvent('nc-cart-drawer:opened'));
    return true;
  }

  function hide() {
    var el = root();
    isOpen = false;
    document.body.classList.remove(BODY_OPEN_CLASS, BODY_VISIBLE_CLASS);
    if (el) {
      el.classList.remove(VISIBLE_CLASS);
      el.setAttribute('aria-hidden', 'true');
    }
    if (lastFocused && typeof lastFocused.focus === 'function' && document.contains(lastFocused)) {
      lastFocused.focus();
    }
    lastFocused = null;
    document.dispatchEvent(new CustomEvent('nc-cart-drawer:closed'));
  }

  function toggle() {
    return isOpen ? hide() : show();
  }

  /* --------------------------------------------------------- badges and events */

  // Mirrors assets/updateCartIconCount.js, which owns this markup today. Kept
  // here as well so the badge is right even on a page where that file is absent.
  function setHeaderCount(count) {
    var link = document.querySelector('.HeaderItem-root-ncv2-cart');
    if (!link) return;

    var node = link.querySelector('.HeaderItem-root_count-ncv2');
    if (!node) {
      node = document.createElement('span');
      node.className = 'HeaderItem-root_count-ncv2';
      node.setAttribute('aria-hidden', 'true');
      (link.querySelector('.HeaderItem-root_iconWrapper-ncv2') || link).appendChild(node);
    }

    node.innerText = count;
    node.classList.toggle('HeaderItem-root_count-ncv2--hidden', count < 1);
  }

  function setDockCount(count) {
    var bubbleHost = document.getElementById('mobile-cart-icon-bubble');
    if (!bubbleHost) return;

    if (count < 1) {
      bubbleHost.innerHTML = '';
      return;
    }

    var bubble = bubbleHost.querySelector('.cart-count-bubble');
    if (!bubble) {
      bubble = document.createElement('span');
      bubble.className = 'cart-count-bubble';
      bubbleHost.appendChild(bubble);
    }
    bubble.classList.toggle('cart-count-bubble--dot', count >= 100);
    bubble.innerHTML = count < 100 ? '<span aria-hidden="true">' + count + '</span>' : '';
  }

  function updateBadges(cart) {
    var count = Number(cart && cart.item_count) || 0;
    setHeaderCount(count);
    setDockCount(count);
  }

  function announce(cart) {
    if (!cart) return cart;
    updateBadges(cart);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    return cart;
  }

  /* ------------------------------------------------------------------ mutations */

  // Every mutation asks for the re-rendered drawer in the same round trip, so
  // the markup can never disagree with the cart it was rendered from.
  function request(url, payload) {
    var body = Object.assign({}, payload, {
      sections: SECTION_ID,
      sections_url: window.location.pathname,
    });

    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (state) {
        if (state && state.sections && state.sections[SECTION_ID]) {
          applyRenderedHTML(state.sections[SECTION_ID]);
          return state;
        }
        return refresh().then(function () {
          return state;
        });
      });
  }

  // Serialise so mutations apply in the order they were clicked. `task` may
  // chain several requests (read the cart, then write it); nothing else runs
  // in between.
  function enqueue(task) {
    var run = queue.then(task);
    // Keep the chain alive even when one link rejects.
    queue = run.catch(function () {});
    return run;
  }

  function mutate(url, payload) {
    return enqueue(function () {
      return request(url, payload);
    });
  }

  // /cart/add.js answers with the added line item; the other endpoints answer
  // with the whole cart. Normalise so callers always get a cart.
  function cartFromState(state) {
    if (state && typeof state.item_count !== 'undefined') return state;
    return null;
  }

  function afterMutation(state) {
    var cart = cartFromState(state);
    if (cart) return Promise.resolve(announce(cart));

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(announce);
  }

  function changeLine(payload) {
    pendingDiscount = null;
    return mutate(CHANGE_URL, payload)
      .then(afterMutation)
      .catch(function (error) {
        console.error('[nc-cart-drawer] cart change failed', error);
        return refresh();
      });
  }

  function addToCart(variantId, options) {
    var opts = options || {};
    var payload = {
      id: Number(variantId) || variantId,
      quantity: opts.quantity || 1,
    };
    if (opts.sellingPlanId) payload.selling_plan = Number(opts.sellingPlanId);
    if (opts.properties) payload.properties = opts.properties;

    pendingDiscount = null;

    return mutate(ADD_URL, payload)
      .then(function (state) {
        // Shopify error payload (sold out, quantity limit): surface it, add nothing.
        if (state && state.status && state.status !== 200) {
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', { detail: { errorMessage: state.description } })
          );
          return null;
        }

        return afterMutation(state).then(function (cart) {
          document.dispatchEvent(
            new CustomEvent('cart:item-added', { detail: { item: state, cart: cart } })
          );
          show();
          return cart;
        });
      })
      .catch(function (error) {
        console.error('[nc-cart-drawer] add to cart failed', error);
        return null;
      });
  }

  // For page flows that bring their own post-add UI (assets/aov-pdp-v4.js):
  // the same queue, section re-render and cart:updated announcement as the
  // drawer's own mutations, but the drawer never opens. Resolves with the
  // cart; rejects with Shopify's description when the cart refuses the change.
  function silentMutation(url, payload) {
    pendingDiscount = null;
    return mutate(url, payload).then(function (state) {
      if (state && state.status && state.status !== 200) {
        throw new Error(state.description || state.message || 'Cart update failed');
      }
      return afterMutation(state);
    });
  }

  /* ------------------------------------------------------------------ discounts */

  // `discount` on /cart/update.js REPLACES every code on the cart, so each
  // operation re-sends the whole list. Codes the shopper did not touch are kept
  // even while inapplicable: campaign LPs apply their code on page load, before
  // anything it covers is in the cart (see initPageDiscount in assets/nc-lp.js).
  function currentDiscountCodes() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        return (cart.discount_codes || []).map(function (entry) {
          return entry.code;
        });
      });
  }

  function sameCode(a, b) {
    return a.toLowerCase() === b.toLowerCase();
  }

  function withoutCode(codes, code) {
    return codes.filter(function (entry) {
      return !sameCode(entry, code);
    });
  }

  function mentionsCode(message, code) {
    return (message || '').split(/\s+/).some(function (word) {
      return sameCode(word.replace(/^["'„(]+|["'”).,!?:;]+$/g, ''), code);
    });
  }

  // Resolves with a CartWarningCode, or null when the reason is unknown. The
  // probe re-sends the cart's applied codes plus the refused one, so every
  // discount warning is about that code; codes merely waiting on the cart (a
  // campaign LP's) are left out and come back with the revert that follows.
  function discountRejectionReason(cart, code) {
    if (!cart || !cart.token) return Promise.resolve(null);

    var codes = (cart.discount_codes || [])
      .filter(function (entry) {
        return entry.applicable && !sameCode(entry.code, code);
      })
      .map(function (entry) {
        return entry.code;
      })
      .concat(code);

    // A probe that never answers would hold the whole mutation queue.
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, DISCOUNT_REASON_TIMEOUT_MS);

    return fetch(STOREFRONT_API_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: DISCOUNT_REASON_QUERY,
        variables: { cartId: 'gid://shopify/Cart/' + cart.token, codes: codes },
      }),
      signal: controller ? controller.signal : undefined,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (result) {
        var payload = result && result.data && result.data.cartDiscountCodesUpdate;
        var warnings = ((payload && payload.warnings) || []).filter(function (warning) {
          return warning.code.indexOf('DISCOUNT_') === 0;
        });
        // Some messages name the code ("Kod rabatowy newsletter nie dotyczy…"),
        // others do not ("Ten rabat nie jest już ważny.").
        var named = warnings.filter(function (warning) {
          return mentionsCode(warning.message, code);
        });
        var warning = named[0] || warnings[0];
        return warning ? warning.code : null;
      })
      .catch(function (error) {
        console.warn('[nc-cart-drawer] discount reason unavailable', error);
        return null;
      })
      .then(function (reason) {
        clearTimeout(timer);
        return reason;
      });
  }

  function discountRejectionMessage(reason, cart, code) {
    var items = (cart && cart.items) || [];
    var others = ((cart && cart.discount_codes) || []).filter(function (entry) {
      return entry.applicable && !sameCode(entry.code, code);
    });
    var key = DISCOUNT_REASON_MESSAGES[reason] ? reason : 'UNKNOWN';

    var notEntitled = key === 'DISCOUNT_NO_ENTITLED_LINE_ITEMS' || key === 'DISCOUNT_INCOMPATIBLE_PURCHASE_TYPE';
    var onlySubscriptions =
      items.length > 0 &&
      items.every(function (item) {
        return !!item.selling_plan_allocation;
      });
    if (notEntitled && onlySubscriptions) key = 'SUBSCRIPTIONS_ONLY';
    if (key === 'DISCOUNT_CODE_NOT_HONOURED' && others.length) key = 'WITH_OTHER_CODE';

    return DISCOUNT_REASON_MESSAGES[key]
      .replace('{code}', code.toUpperCase())
      .replace('{other}', others.length ? others[0].code.toUpperCase() : '');
  }

  function applyDiscount(code) {
    var trimmed = (code || '').trim();
    if (!trimmed) return Promise.resolve();

    pendingDiscount = null;

    return enqueue(function () {
      return currentDiscountCodes().then(function (existing) {
        var codes = withoutCode(existing, trimmed).concat(trimmed);

        return request(UPDATE_URL, { discount: codes.join(',') }).then(function (state) {
          var cart = cartFromState(state);
          var rejected = ((cart && cart.discount_codes) || []).some(function (entry) {
            return sameCode(entry.code, trimmed) && entry.applicable === false;
          });
          if (!rejected) return state;

          // Rebuy validated a code before applying it, so a typo never cost the
          // shopper a working code or followed them to checkout. Put the cart's
          // codes back as they were; pendingDiscount carries the message
          // through that re-render.
          return discountRejectionReason(cart, trimmed).then(function (reason) {
            pendingDiscount = {
              code: trimmed,
              message: discountRejectionMessage(reason, cart, trimmed),
            };
            return request(UPDATE_URL, { discount: existing.join(',') });
          });
        });
      });
    })
      .then(afterMutation)
      .catch(function (error) {
        console.error('[nc-cart-drawer] discount failed', error);
        // Re-render: the submit handler disabled the form, and only a render
        // brings it back.
        return refresh();
      });
  }

  function removeDiscount(code) {
    pendingDiscount = null;

    return enqueue(function () {
      return currentDiscountCodes().then(function (existing) {
        return request(UPDATE_URL, { discount: withoutCode(existing, code || '').join(',') });
      });
    })
      .then(afterMutation)
      .catch(function (error) {
        console.error('[nc-cart-drawer] discount removal failed', error);
        return refresh();
      });
  }

  /* ------------------------------------------------------------------- checkout */

  // Longest a pending cart change may hold the shopper back from checkout.
  var CHECKOUT_WAIT_MS = 4000;

  // Checkout reads the cart on the server, so a change still in flight when
  // the button is clicked (a quick "USUŃ", then "Realizuj zamówienie") would
  // open checkout on the cart from before it. Let queued mutations land first.
  function checkout() {
    var timeout = new Promise(function (resolve) {
      setTimeout(resolve, CHECKOUT_WAIT_MS);
    });
    Promise.race([queue, timeout]).then(function () {
      window.location = '/checkout';
    });
  }

  /* ------------------------------------------------------------------ delegation */

  function itemOf(element) {
    return element.closest('[data-nc-cart-item]');
  }

  function currentQuantity(item) {
    var key = item.dataset.lineKey;
    if (Object.prototype.hasOwnProperty.call(optimisticQuantities, key)) {
      return optimisticQuantities[key];
    }
    return Number(item.dataset.quantity) || 1;
  }

  /* ------------------------------------------------------------ busy states */

  /*
   * Rebuy showed four different "working on it" affordances:
   *
   *   stepper +/-   the clicked button's glyph becomes a spinning fa-sync-alt
   *                 ("far fa-plus" -> "far fa-sync-alt fa-fast-spin"), ~1s of
   *                 spin on a real request
   *   subscription  the toggle's label text becomes "Zamiana..."
   *   remove        the button's label becomes "USUWANIE…" (v-if on
   *                 item.status == 'removing' in the captured cart_items
   *                 template — note the ellipsis is U+2026, not three dots)
   *   discount      the input AND the submit button both get `disabled`
   *                 (.rebuy-button:disabled is the 50%-opacity state)
   *
   * The first, second and fourth were read off the live drawer with a
   * MutationObserver (2026-08-28) rather than guessed — they are Vue state and
   * do not appear in the captured templates at all.
   *
   * None of them need undoing: every mutation ends in a re-render that replaces
   * these nodes wholesale. A failed one lands in refresh(), which re-renders too.
   */
  var SUBSCRIPTION_BUSY_LABEL = 'Zamiana...';
  var REMOVE_BUSY_LABEL = 'USUWANIE…';
  var BOOST_BUSY_LABEL = 'Dodawanie…';

  function markIconBusy(button) {
    var icon = button.querySelector('i');
    if (icon) icon.className = 'far fa-sync-alt fa-fast-spin';
  }

  function markTextBusy(button, selector, text) {
    var label = button.querySelector(selector);
    if (label) label.textContent = text;
  }

  function markDiscountFormBusy(form) {
    var controls = form.querySelectorAll('input, button');
    for (var i = 0; i < controls.length; i++) controls[i].disabled = true;
  }

  function onClick(event) {
    var target = event.target;

    if (target.closest('[data-nc-cart-close]')) {
      event.preventDefault();
      hide();
      return;
    }

    var quantityButton = target.closest('[data-nc-cart-quantity]');
    if (quantityButton) {
      event.preventDefault();
      var qtyItem = itemOf(quantityButton);
      if (!qtyItem) return;

      var step = quantityButton.dataset.ncCartQuantity === 'up' ? 1 : -1;
      var next = Math.max(0, currentQuantity(qtyItem) + step);
      optimisticQuantities[qtyItem.dataset.lineKey] = next;

      markIconBusy(quantityButton);
      changeLine({ id: qtyItem.dataset.lineKey, quantity: next });
      return;
    }

    var removeButton = target.closest('[data-nc-cart-remove]');
    if (removeButton) {
      event.preventDefault();
      var removeItem = itemOf(removeButton);
      if (!removeItem) return;
      optimisticQuantities[removeItem.dataset.lineKey] = 0;
      markTextBusy(removeButton, 'span', REMOVE_BUSY_LABEL);
      changeLine({ id: removeItem.dataset.lineKey, quantity: 0 });
      return;
    }

    var toggleButton = target.closest('[data-nc-cart-subscription-toggle]');
    if (toggleButton) {
      event.preventDefault();
      var subItem = itemOf(toggleButton);
      if (!subItem) return;
      var isSubscribed = toggleButton.getAttribute('aria-pressed') === 'true';
      markTextBusy(toggleButton, '.rebuy-cart__subscription-label', SUBSCRIPTION_BUSY_LABEL);
      changeLine({
        id: subItem.dataset.lineKey,
        quantity: currentQuantity(subItem),
        selling_plan: isSubscribed ? null : Number(subItem.dataset.defaultSellingPlan),
      });
      return;
    }

    // "Dodaj i wskocz" rows: a plain add; the re-render brings the next tier's
    // suggestion. A refused add (sold out, limit) re-renders to reset the row.
    var boostButton = target.closest('[data-nc-cart-boost-add]');
    if (boostButton) {
      event.preventDefault();
      boostButton.disabled = true;
      markTextBusy(boostButton, 'span', BOOST_BUSY_LABEL);
      addToCart(boostButton.getAttribute('data-nc-cart-boost-add')).then(function (cart) {
        if (!cart) refresh();
      });
      return;
    }

    var discountRemove = target.closest('[data-nc-discount-remove]');
    if (discountRemove) {
      event.preventDefault();
      removeDiscount(discountRemove.getAttribute('data-nc-discount-remove'));
      return;
    }

    if (target.closest('[data-nc-cart-checkout]')) {
      event.preventDefault();
      checkout();
    }
  }

  function onChange(event) {
    var select = event.target.closest('[data-nc-cart-frequency]');
    if (!select) return;

    var item = itemOf(select);
    if (!item) return;

    var value = select.value;
    changeLine({
      id: item.dataset.lineKey,
      quantity: currentQuantity(item),
      selling_plan: value === 'onetime' ? null : Number(value),
    });
  }

  function onSubmit(event) {
    var form = event.target.closest('[data-nc-discount-form]');
    if (!form) return;
    event.preventDefault();
    var input = form.querySelector('input[name="discount"]');
    var code = input && input.value;

    // Guard on a real code: applyDiscount short-circuits on an empty one
    // without re-rendering, which would leave the form disabled for good.
    if (code && code.trim()) markDiscountFormBusy(form);
    applyDiscount(code);
  }

  function onKeydown(event) {
    if (!isOpen) return;

    if (event.key === 'Escape') {
      hide();
      return;
    }

    if (event.key !== 'Tab') return;

    // Focus trap: the drawer is a modal dialog, so Tab must stay inside it.
    var flyout = root() && root().querySelector('.rebuy-cart__flyout');
    if (!flyout) return;

    var focusable = Array.prototype.filter.call(
      flyout.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      function (el) {
        return el.offsetParent !== null;
      }
    );
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* --------------------------------------------------------------- cart triggers */

  // The home page is redirected to /collections by
  // an inline script in theme.liquid, so opening the drawer there would be lost
  // in the reload — send the shopper to /collections and open it on arrival.
  function openFromTrigger() {
    if (document.body.classList.contains('template-index')) {
      window.location.href = '/collections?opencart=1';
      return true;
    }
    return show();
  }

  function onCartTriggerClick(event) {
    var trigger = event.target.closest('.HeaderItem-root-ncv2-cart, .dock__item[href*="/cart"]');
    if (!trigger) return;

    // If the section failed to render, let the link do what it says and go to
    // /cart — the native cart page still works as a fallback.
    if (!root() && !document.body.classList.contains('template-index')) return;

    event.preventDefault();
    openFromTrigger();
  }

  // This file is deferred and queued behind vendor-v4.js and global.js, so the
  // header is painted and clickable for a while before anything here binds. The
  // early trigger in sections/nc-header-main.liquid holds any click that lands
  // in that window — otherwise the <a href="/cart"> just navigates, which is the
  // "cart icon goes to /cart instead of opening the drawer" bug.
  function consumePendingTrigger() {
    if (!window.ncCartDrawerPendingOpen) return;
    window.ncCartDrawerPendingOpen = false;

    // Drawer missing after all: honour what the link said rather than swallowing
    // the click the early trigger already cancelled.
    if (!openFromTrigger()) window.location.href = '/cart';
  }

  function openFromURL() {
    if (!/[?&](opencart=1|viewcart=true)/.test(location.search)) return;

    show();
    try {
      var url = new URL(location.href);
      url.searchParams.delete('opencart');
      url.searchParams.delete('viewcart');
      history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch (error) {
      /* URL rewriting is cosmetic — never let it stop the drawer opening. */
    }
  }

  /* ------------------------------------------------------------------------ init */

  // A page flow may take over the moment after an add (window.NcPdpFlow, set
  // by assets/aov-pdp-v4.js on the test-7 PDP): the drawer still re-renders so
  // it is current, it just stays closed. A flow that throws is ignored.
  function claimedByPageFlow(product) {
    if (!product || !window.NcPdpFlow || typeof window.NcPdpFlow.claim !== 'function') return false;
    try {
      return window.NcPdpFlow.claim(product) === true;
    } catch (error) {
      console.error('[nc-cart-drawer] page flow failed, opening the drawer', error);
      return false;
    }
  }

  function onProductAdded(event) {
    var product = (event.detail && event.detail.product) || null;
    var bundled = product && product.sections && product.sections[SECTION_ID];
    var claimed = claimedByPageFlow(product);
    var open = function () {
      if (!claimed) show();
    };

    // The PDP form bundles the re-render into its own add request; other add
    // paths do not, so fetch it for them.
    if (bundled) {
      applyRenderedHTML(bundled);
      open();
    } else {
      refresh().then(open);
    }

    if (product) {
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item: product } }));
    }

    // These adds happen outside our own mutation path, so nothing has announced
    // the new cart yet.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(announce)
      .catch(function () {
        /* Badge counts are cosmetic — never let this break the add. */
      });
  }

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('click', onCartTriggerClick, true);
    document.addEventListener('change', onChange);
    document.addEventListener('submit', onSubmit);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('ajaxProduct:added', onProductAdded);

    // Something else changed the cart (the cart page, a legacy add path): keep
    // in step without opening. Our own mutations attach the cart to the event,
    // so skipping those avoids re-rendering what we just rendered.
    document.addEventListener('cart:updated', function (event) {
      if (event.detail && event.detail.cart) return;
      refresh();

      // These events carry no cart, so the header badge's own listener
      // (updateCartIconCount.js) skips them. Badges only — re-announcing would
      // hand every cart:updated listener the same change twice.
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (response) {
          return response.json();
        })
        .then(updateBadges)
        .catch(function () {
          /* Badge counts are cosmetic. */
        });
    });

    openFromURL();
    consumePendingTrigger();
  }

  window.NcCartDrawer = {
    show: show,
    hide: hide,
    toggle: toggle,
    refresh: refresh,
    addToCart: addToCart,
    isEnabled: enabled,
    checkout: checkout,
    silentAdd: function (items) {
      return silentMutation(ADD_URL, { items: items });
    },
    silentChange: function (payload) {
      return silentMutation(CHANGE_URL, payload);
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
